import React, { useEffect, useRef, useState } from 'react';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { LucidCfg, LucidState, calc, dayTag, ddMessage, fmt, loadState, saveState, statusOf, todayMessage } from './lucidflex';
import s from './css/CalculadoraLucid.module.css';

// Teclado decimal del móvil: acepta coma y punto, y un signo menos opcional delante
const DECIMAL_RE = /^-?\d*\.?\d*$/;
const normalize = (raw: string) => raw.trim().replace(',', '.');

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).map(n => s[n as string]).join(' ');

interface CommitFieldProps {
    value: number;
    onCommit: (raw: string) => void;
    label?: string;
    ariaLabel?: string;
    className?: string;
    step?: string;
    // true: el valor se reescribe en cada render (como las filas de días, que el original reconstruía)
    sync?: boolean;
}

// Campo que escucha el evento nativo 'change' (al confirmar: blur, Enter, flechas)
// en lugar de cada tecla, igual que la calculadora original.
function CommitField({ value, onCommit, label, ariaLabel, className, step, sync }: CommitFieldProps) {
    const ref = useRef<HTMLInputElement>(null);
    const commitRef = useRef(onCommit);
    commitRef.current = onCommit;

    useEffect(() => {
        const el = ref.current!;
        const h = () => commitRef.current(el.value);
        el.addEventListener('change', h);
        return () => el.removeEventListener('change', h);
    }, []);

    useEffect(() => {
        if (sync && ref.current) ref.current.value = String(value);
    });

    return (
        <TextField
            inputRef={ref}
            defaultValue={value}
            label={label}
            type="text"
            size="small"
            fullWidth
            className={className}
            inputProps={{ step, inputMode: 'decimal', 'aria-label': ariaLabel }}
        />
    );
}

const CFG_FIELDS: [keyof LucidCfg, string][] = [
    ['start', 'Balance inicial'],
    ['target', 'Objetivo'],
    ['mll', 'Pérdida máxima'],
    ['cons', 'Consistencia %'],
];

const CalculadoraLucid: React.FC = () => {
    const [st, setSt] = useState<LucidState>(loadState);
    const [newDay, setNewDay] = useState('');
    const [armed, setArmed] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(timer.current), []);

    const update = (next: LucidState) => { saveState(next); setSt(next); };

    const r = calc(st);
    const status = statusOf(r);
    const mg = r.bal - r.mllv;

    const onNewDayChange = (raw: string) => {
        const v = normalize(raw);
        if (DECIMAL_RE.test(v)) setNewDay(v);
    };

    // El teclado decimal del móvil no tiene tecla "-": el botón ± cambia el signo
    const toggleSign = () => setNewDay(v => (v.startsWith('-') ? v.slice(1) : '-' + v));

    const onAdd = (e: React.FormEvent) => {
        e.preventDefault();
        const x = parseFloat(newDay);
        if (!isFinite(x) || x === 0) return;
        update({ ...st, days: [...st.days, x] });
        setNewDay('');
    };

    const onReset = () => {
        if (!armed) {
            setArmed(true);
            timer.current = setTimeout(() => setArmed(false), 3000);
            return;
        }
        setArmed(false);
        update({ ...st, days: [] });
    };

    const setDay = (i: number, raw: string) => {
        const x = parseFloat(normalize(raw));
        if (isFinite(x)) update({ ...st, days: st.days.map((v, j) => (j === i ? x : v)) });
    };

    const delDay = (i: number) => update({ ...st, days: st.days.filter((_, j) => j !== i) });

    const setCfg = (k: keyof LucidCfg, raw: string) => {
        const x = parseFloat(normalize(raw));
        if (isFinite(x) && x > 0) update({ ...st, cfg: { ...st.cfg, [k]: x } });
    };

    return (
        <div className="container-fluid">
            <div className="text-center">
                <h2>Calculadora Consistencia</h2>
            </div>

            <div className={cx('wrap') + ' container-fluid pb-3'}>
                <section className={cx('card', 'status', status.cls)}>
                    <div className={s.cardHead}>
                        <span className={s.lbl}>Beneficio total</span>
                        <span className={cx('pill', status.cls)}>{status.txt}</span>
                    </div>
                    <div className={cx('big', 'num')}>{fmt(r.T)}</div>
                    <div className={s.bar}><i style={{ width: Math.max(0, Math.min(100, r.T / r.req * 100)) + '%' }} /></div>
                    <div className={s.barlbl}><span>$0</span><span className={s.num}>Necesitas {fmt(r.req)}</span></div>
                    <div className={s.stats}>
                        <div><span className={s.lbl}>Mejor día</span><span className={cx('v', 'num')}>{fmt(r.best)}</span></div>
                        <div><span className={s.lbl}>Consistencia</span><span className={cx('v', 'num')}>{r.T > 0 ? (r.best / r.T * 100).toFixed(1) + '%' : '—'}</span></div>
                        <div><span className={s.lbl}>Te falta</span><span className={cx('v', 'num')}>{r.passed ? '$0' : fmt(r.remaining)}</span></div>
                    </div>
                </section>

                <section className={cx('card', 'today')}>
                    <span className={s.lbl}>Hoy</span>
                    <div className={s.todayRow}><p>Tope de ganancia sin empeorar la consistencia</p><span className={cx('k', 'num')}>{fmt(r.cap)}</span></div>
                    <p className={s.msg}>{todayMessage(r)}</p>
                </section>

                <section className={s.card}>
                    <span className={s.lbl}>Días (P&amp;L neto, después de comisiones)</span>
                    <form className={s.addForm} onSubmit={onAdd}>
                        <TextField
                            label="Resultado del día"
                            placeholder="Ej. 450 o -120"
                            type="text"
                            fullWidth
                            value={newDay}
                            onChange={e => onNewDayChange(e.target.value)}
                            inputProps={{ inputMode: 'decimal' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <button
                                            type="button"
                                            className={cx('signo', newDay.startsWith('-') && 'negativo')}
                                            onClick={toggleSign}
                                            aria-label="Cambiar signo (ganancia / pérdida)"
                                        >
                                            ±
                                        </button>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <div className={s.botones}>
                            <button className="reset-button" type="button" onClick={onReset}>
                                {armed ? '¿Seguro? Toca otra vez' : 'Borrar días'}
                            </button>
                            <button className="main-button" type="submit">Añadir día</button>
                        </div>
                    </form>
                    <div className={s.days}>
                        {!st.days.length && <p className={s.empty}>Aún no hay días. Añade el resultado neto de cada sesión.</p>}
                        {st.days.map((v, i) => {
                            const tag = dayTag(v, r);
                            return (
                                <div className={s.day} key={i}>
                                    <span className={s.n}>Día {i + 1}</span>
                                    <CommitField
                                        className={s.dayInput}
                                        step="0.01"
                                        ariaLabel={'Día ' + (i + 1)}
                                        value={v}
                                        sync
                                        onCommit={raw => setDay(i, raw)}
                                    />
                                    <span className={cx('tag', tag.cls && 'pill', tag.cls)}>{tag.txt}</span>
                                    <IconButton size="small" className={s.del} aria-label={'Borrar día ' + (i + 1)} onClick={() => delDay(i)}>✕</IconButton>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <section className={cx('card', 'dd')}>
                    <span className={s.lbl}>Drawdown (cierre de sesión)</span>
                    <div className={s.stats}>
                        <div><span className={s.lbl}>Balance</span><span className={cx('v', 'num')}>{fmt(r.bal)}</span></div>
                        <div><span className={s.lbl}>Límite (MLL)</span><span className={cx('v', 'num')}>{fmt(r.mllv)}</span></div>
                        <div><span className={s.lbl}>Margen</span><span className={cx('v', 'num', mg < 300 && 'bad')}>{fmt(mg)}</span></div>
                    </div>
                    <p className={s.foot}>{ddMessage(r)}</p>
                </section>

                <details className={s.card}>
                    <summary>Ajustes de la cuenta</summary>
                    <div className={s.cfg}>
                        {CFG_FIELDS.map(([k, label]) => (
                            <CommitField key={k} label={label} value={st.cfg[k]} onCommit={raw => setCfg(k, raw)} />
                        ))}
                    </div>
                </details>

                <p className={s.foot}>Regla: mejor día ÷ beneficio total ≤ 40%. Si te pasas no pierdes la cuenta, solo necesitas más beneficio total (mejor día × 2,5). Los datos se guardan solo en este navegador.</p>
            </div>
        </div>
    );
};

export default CalculadoraLucid;
